import React, { useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import SuperTreeview from 'react-super-treeview'
import TextField from '@mui/material/TextField'
import 'react-super-treeview/dist/style.css'
import { EMPTY_OBJECT } from './AddTaskList'

function SegmentationBuilder(props) {
    const [sqLDbList, setSqLDbList] = useState([])
    const [databaseId, setDatabaseId] = useState(-1)
    const [dataSources, setDataSources] = useState([])
    const [dataSourceSystemName, setDataSourceSystemName] = useState('')
    const [dimensions, setDimensions] = useState([])
    
    // UI State Variables
    const [isLoadingRequest, setIsLoadingRequest] = useState(false)
    const [segmentationData, setSegmentationData] = useState([])
    const [currentCheckedNodeId, setCurrentCheckedNodeId] = useState('')
    const [addingNewSegment, setAddingNewSegment] = useState(false)
    const [addingNewSegmentValue, setAddingNewSegmentValue] = useState(false)
    const [renamingElement, setRenamingElement] = useState(false)
    const [newSegmentName, setNewSegmentName] = useState('')
    const [newSegmentValue, setNewSegmentValue] = useState('')
    const [renameValue, setRenameValue] = useState('')
    const [currentCheckedDepth, setCurrentCheckedDepth] = useState(-1)
    const [keyId, setKeyId] = useState(0)

    //#region useEffect
    // trigger on component mount
    useEffect(() => {
        if (sqLDbList.length === 0) {
            let dBList = props.databases.filter(x => x.serverType == 0)
            setSqLDbList(dBList)
        }

        // This is true if we are in edit mode
        if (props.taskData.databaseId !== -1) {
            if (props.taskData.databaseId !== databaseId) {
                callForPropDataToLoadExisting()
            }
        } else {
            resetStateData()
        }
    }, [])
    useEffect(() => {
        // This is true if we are in edit mode
        if (props.taskData.databaseId !== -1) {
            if (props.taskData.databaseId !== databaseId) {
                callForPropDataToLoadExisting()
            }
        } else {
            resetStateData()
        }
    }, [props.taskData.databaseId, props.taskData.segmentationDeclaration])
    useEffect(() => {
        if (dimensions.length === 0) {
            setAddingNewSegment(false)
            setAddingNewSegmentValue(false)
            setRenamingElement(false)
            setNewSegmentName('')
            setNewSegmentValue('')
            setRenameValue('')
            setCurrentCheckedNodeId('')
            setSegmentationData([])
            setCurrentCheckedDepth(-1)
            setKeyId(0)
        }
    }, [dimensions])
    useEffect(() => {
        let copied = handleCheckedReset(segmentationData, currentCheckedNodeId)
        setSegmentationData(copied)
        setKeyId(prevKeyId => prevKeyId + 1)
    }, [currentCheckedNodeId])
    //#endregion

    /**
     * If component has a prop for the [onSave] event, the purpose of this is to fire that event with the latest information
     * @param {int} dbId - Selected database ID
     * @param {string} dsSystemName - Data source system name from the selected database
     */
    function fireOnSaveEvent(dbId, dsSystemName, treeData) {
        if (props.onSave) {
            let copied = JSON.parse(JSON.stringify(EMPTY_OBJECT))
            copied["databaseId"] = dbId
            copied["dataSourceSystemName"] = dsSystemName
            copied["segmentationDeclaration"] = treeData
            props.onSave(copied)
        }
    }

    //#region State Set Methods
    function resetStateData() {
        setDatabaseId(-1)
        setDataSources([])
        setDataSourceSystemName('')
        setDimensions([])
        setSegmentationData([])
        setCurrentCheckedDepth(-1)
        setIsLoadingRequest(false)
    }
    function callForPropDataToLoadExisting() {
        setCurrentCheckedNodeId('')
        setDatabaseId(props.taskData.databaseId)
        setIsLoadingRequest(true)

        let getDataSources = props.getDataSources(props.taskData.databaseId)
        getDataSources.then((resp) => { 
            if (resp.status !== 200) {
                throw new Error(resp)
            } else {
                return resp.json()
            }
        }).then((json) => {
            setDataSources(json)
            setDataSourceSystemName(props.taskData.dataSourceSystemName)
            setDimensions([])
            setSegmentationData([])
            setCurrentCheckedDepth(-1)

            let getDimensions = props.getDimensions(props.taskData.databaseId, props.taskData.dataSourceSystemName)
            getDimensions.then((resp) => { 
                if (resp.status !== 200) {
                    throw new Error(resp)
                } else {
                    return resp.json()
                }
            }).then((json) => {
                let dimensionsFromDataSource = json.filter(x => x.typeOfDimension !== -1)
                let newPreBuiltTreeView = []

                if (props.taskData.segmentationDeclaration && props.taskData.segmentationDeclaration.length > 0) {
                    newPreBuiltTreeView = props.taskData.segmentationDeclaration
                } else {
                    for (let dimIndex = 0; dimIndex < dimensionsFromDataSource.length; dimIndex++) {
                        let current = dimensionsFromDataSource[dimIndex]
                        let builtTreeData = getSegmentationTreeNodeStructure(current["segments"], dimIndex, '', current["typeOfDimension"])
                        let nextNode = {
                            id: current["typeOfDimension"],
                            name: current["alias"],
                            isChecked: false,
                            isExpanded: true,
                            dimensionType: current["typeOfDimension"],
                            oldName: '',
                            value: '',
                            dataType: '',
                            children: builtTreeData
                        }
                        newPreBuiltTreeView.push(nextNode)
                    }
                }

                setIsLoadingRequest(false)
                setDimensions(dimensionsFromDataSource)
                setSegmentationData(newPreBuiltTreeView)
                fireOnSaveEvent(props.taskData.databaseId, props.taskData.dataSourceSystemName, newPreBuiltTreeView)
            }).catch((ex) => {
                console.error(ex)
                resetStateData()
            })
        }).catch((ex) => {
            console.error(ex)
            resetStateData()
        })
    }
    //#endregion

    //#region Render Methods
    function shouldTheAddSegmentButtonBeEnabled(dimensionData, checkedNode) {
        let areThereDimensions = dimensionData.length > 0
        let isTheSelectedNodeNumeric = (typeof checkedNode === "number")
        return areThereDimensions && isTheSelectedNodeNumeric
    }
    function shouldDisableNewValueButton(depth) {
        return depth !== 1
    }
    function shouldDisableRenameButton(depth) {
        return depth === -1 || depth === 0
    }
    function shouldDisableAddNewSegmentAddButton(enteredSegmentName, checkId, treeData, dimensionData) {
        if (stringIsEmpty(enteredSegmentName)) { return true }

        let dimensionType = getDimensionTypeFromCheckedNode(checkId, treeData)
        if (dimensionType > -1) {
            let treeStructureIndex = getLevelOneTreeStructureIndex(dimensionData, dimensionType)
            
            if (treeData[treeStructureIndex]["children"].length == 0) { return false }
            if (treeData[treeStructureIndex]["children"].filter(x => stringAreEqual(x.name, enteredSegmentName)).length > 0) { return true }
        }
        
        return false
    }
    function shouldDisableAddNewSegmentAction(enteredSegmentValue, checkId, treeData, dimensionData) {
        if (stringIsEmpty(enteredSegmentValue)) { return true }

        let dimensionType = getDimensionTypeFromCheckedNode(checkId, treeData)
        if (dimensionType > -1) {
            let treeStructureIndex = getLevelOneTreeStructureIndex(dimensionData, dimensionType)
            if (treeData[treeStructureIndex]["children"].length == 0) { return false }
            let segmentIndex = getLevelTwoTreeStructureIndex(treeData, treeStructureIndex, checkId)
            let lookup = treeData[treeStructureIndex]["children"][segmentIndex]["children"].filter(x => x.name.toString().toUpperCase() === enteredSegmentValue.toString().toUpperCase())

            if (lookup.length > 0) { return true }
        }
        
        return false
    }
    function shouldDisableRenameAdd(rename, depth, checkId, treeData, dimensionData) {
        if (rename === '') { return true }

        let dimensionType = getDimensionTypeFromCheckedNode(checkId, treeData)
        if (dimensionType > -1) {
            let treeStructureIndex = getLevelOneTreeStructureIndex(dimensionData, dimensionType)

            if (depth === 1) {
                if (treeData[treeStructureIndex]["children"].filter(x => x.name === rename).length > 0) {
                    return true
                }
            } else if (depth === 2) {
                let segmentIndex = getIndexOfSegmentWithMatchingId(checkId, treeData[treeStructureIndex]["children"])
                let doesThisNameAlreadyExists = treeData[treeStructureIndex]["children"][segmentIndex]["children"].filter(x => x.name === rename).length > 0
                let isThisAnIntSegmentButValueIsNotAnInt = treeData[treeStructureIndex]["children"][segmentIndex]["dataType"] === 'int' && !isValidInteger(rename)

                if (isThisAnIntSegmentButValueIsNotAnInt) {
                    return true
                } else if (doesThisNameAlreadyExists) {
                    return true
                }
            }
        }

        return false
    }
    function getDimensionAlias(dimensionData, checkedNode) {
        let areThereDimensions = dimensionData.length > 0
        let isTheSelectedNodeNumeric = (typeof checkedNode === "number")
        
        if (areThereDimensions && isTheSelectedNodeNumeric) {
            let dimensionLookup = getDimentionFromDimensionType(dimensionData, checkedNode)
            if (dimensionLookup) {
                return dimensionLookup["alias"]
            }
        }

        return null
    }
    function getDimensionAliasFromCheckedNodeId(checkedId, dimensionData, treeData) {
        let dimensionType = getDimensionTypeFromCheckedNode(checkedId, treeData)
        if (dimensionType > -1) {
            return getDimensionAlias(dimensionData, dimensionType)
        }

        return null
    }
    function getLevelOneTreeStructureIndex(treeData, checkedNode) {
        for (let treeIndex = 0; treeIndex < treeData.length; treeIndex++) {
            let current = treeData[treeIndex]
            if (current["typeOfDimension"] === checkedNode) {
                return treeIndex
            }
        }

        return -1
    }
    function getLevelTwoTreeStructureIndex(treeData, levelOneIndex, checkedNode) {
        for (let treeIndex = 0; treeIndex < treeData[levelOneIndex]["children"].length; treeIndex++) {
            let current = treeData[levelOneIndex]["children"][treeIndex]
            if (current["id"] === checkedNode) {
                return treeIndex
            }
        }

        return -1
    }
    function getIndexOfSegmentWithMatchingId(checkedNode, treeData) {
        for (let treeItr = 0; treeItr < treeData.length; treeItr++) {
            let children = treeData[treeItr]["children"]
            let lookup = children.filter(x => x.id === checkedNode)
            if (lookup.length > 0) {
                return treeItr
            }
        }
        
        return -1
    }
    //#endregion

    //#region Handlers Events
    function handleDatabase(event) {
        event.preventDefault()
        setCurrentCheckedNodeId('')
        setDatabaseId(event.target.value)
        setIsLoadingRequest(true)
        setDataSources([])
        setDataSourceSystemName('')
        setSegmentationData([])
        setCurrentCheckedDepth(-1)

        let getDataSources = props.getDataSources(event.target.value)
        getDataSources.then((resp) => { 
            if (resp.status !== 200) {
                throw new Error(resp)
            } else {
                return resp.json()
            }
        }).then((json) => {
            setDataSources(json)
            setIsLoadingRequest(false)
            fireOnSaveEvent(event.target.value, '', [])
        }).catch((ex) => {
            console.error(ex)
            setDataSources([])
            setIsLoadingRequest(false)
        })
    }
    function handleDataSourceChange(event) {
        event.preventDefault()
        setCurrentCheckedNodeId('')
        setDataSourceSystemName(event.target.value)
        setDimensions([])
        setSegmentationData([])
        setCurrentCheckedDepth(-1)
        setIsLoadingRequest(true)

        let getDimensions = props.getDimensions(databaseId, event.target.value)
        getDimensions.then((resp) => { 
            if (resp.status !== 200) {
                throw new Error(resp)
            } else {
                return resp.json()
            }
        }).then((json) => {
            let dimensionsFromDataSource = json.filter(x => x.typeOfDimension !== -1)

            let newPreBuiltTreeView = []
            
            for (let dimIndex = 0; dimIndex < dimensionsFromDataSource.length; dimIndex++) {
                let current = dimensionsFromDataSource[dimIndex]
                let builtTreeData = getSegmentationTreeNodeStructure(current["segments"], dimIndex, '', current["typeOfDimension"])
                let nextNode = {
                    id: current["typeOfDimension"],
                    name: current["alias"],
                    isChecked: false,
                    isExpanded: true,
                    dimensionType: current["typeOfDimension"],
                    oldName: '',
                    value: '',
                    dataType: '',
                    children: builtTreeData
                }
                newPreBuiltTreeView.push(nextNode)
            }

            setIsLoadingRequest(false)
            setDimensions(dimensionsFromDataSource)
            setSegmentationData(newPreBuiltTreeView)
            fireOnSaveEvent(databaseId, event.target.value, newPreBuiltTreeView)
        }).catch((ex) => {
            console.error(ex)
            setDataSources([])
            setDimensions([])
            setSegmentationData([])
            setCurrentCheckedDepth(-1)
            setIsLoadingRequest(false)
        })
    }
    //#endregion

    //#region Data Methods
    function getSegmentationTreeNodeStructure(segments, dimensionIndex, checkedId, dimensionType) {
        let rtnVal = []

        for (let segmentIndex = 0; segmentIndex < segments.length; segmentIndex++) {
            let currentSegment = segments[segmentIndex]
            let currentSegmentMembers = currentSegment["memberModels"]
            let segmentChildrenNodes = []

            for (let memberIndex = 0; memberIndex < currentSegmentMembers.length; memberIndex++) {
                let currentSegmentMember = currentSegmentMembers[memberIndex]
                segmentChildrenNodes.push({
                    id: `${dimensionIndex}-${segmentIndex}-${memberIndex}`,
                    name: currentSegmentMember["alias"],
                    isChecked: checkedId === `${dimensionIndex}-${segmentIndex}-${memberIndex}`,
                    segmentName: currentSegment["alias"],
                    value: currentSegment["systemName"],
                    dimensionType: dimensionType,
                    oldName: '',
                    dataType: currentSegment["segmentDataType"],
                    children: []
                })
            }

            let segmentNode = {
                id: `${dimensionIndex}-${segmentIndex}`,
                name: currentSegment["alias"],
                isExpanded: true,
                isChecked: checkedId === `${dimensionIndex}-${segmentIndex}`,
                value: currentSegment["systemName"],
                dimensionType: dimensionType,
                oldName: '',
                dataType: currentSegment["segmentDataType"],
                children: segmentChildrenNodes
            }

            rtnVal.push(segmentNode)
        }

        return rtnVal
    }
    function handleCheckedReset(treeData, checkedId) {
        let rtnVal = []

        for (let treeParentIndex = 0; treeParentIndex < treeData.length; treeParentIndex++) {
            let currentNode = treeData[treeParentIndex]
            let children = treeData[treeParentIndex]["children"]

            currentNode["isChecked"] = currentNode["id"] === checkedId

            for (let treeChildIndex = 0; treeChildIndex < children.length; treeChildIndex++) {
                children[treeChildIndex]["isChecked"] = children[treeChildIndex]["id"] === checkedId

                let subChildren = children[treeChildIndex]["children"]
                for (let subChildIndex = 0; subChildIndex < subChildren.length; subChildIndex++) {
                    subChildren[subChildIndex]["isChecked"] = subChildren[subChildIndex]["id"] === checkedId
                }
            }

            rtnVal.push(currentNode)
        }

        return rtnVal
    }
    function getDimentionFromDimensionType(dimensionData, checkedNode) {
        let lookup = dimensionData.filter(x => x["typeOfDimension"] === checkedNode)

        if (lookup) {
            return lookup[0]
        }

        return null
    }
    function getDimensionTypeFromCheckedNode(checkedId, treeData) {
        let rtnVal = -1

        for (let nodeItr = 0; nodeItr < treeData.length; nodeItr++) {
            let current = treeData[nodeItr]
            let childrenNodes = current["children"]
            let catcher = -1
            if (current["id"] === checkedId && catcher === -1) {
                catcher = current["dimensionType"]
            }
            if (catcher > -1) {
                return catcher
            } else if (childrenNodes) {
                catcher = getDimensionTypeFromCheckedNode(checkedId, childrenNodes)
                if (catcher > -1) {
                    return catcher
                }
            }
        }

        return rtnVal
    }
    function getNameCheckedNode(checkedId, treeData) {
        let rtnVal = ''

        for (let nodeItr = 0; nodeItr < treeData.length; nodeItr++) {
            let current = treeData[nodeItr]
            let childrenNodes = current["children"]
            let catcher = ''
            if (current["id"] === checkedId && catcher === '') {
                catcher = current["name"]
            }
            if (catcher !== '') {
                return catcher
            } else if (childrenNodes) {
                catcher = getNameCheckedNode(checkedId, childrenNodes)
                if (catcher !== '') {
                    return catcher
                }
            }
        }

        return rtnVal
    }
    function getSegmentNameFromIndexes(treeData, levelOne, levelTwo) {
        return treeData[levelOne]["children"][levelTwo]["name"]
    }
    function getRenameTitle(checkedNodeDepth, checkedId, treeData, dimensionData) {
        if (checkedNodeDepth === 1) {
            return getRenameLevelTwoTitle(checkedId, treeData, dimensionData)
        } else if (checkedNodeDepth === 2) {
            return getRenameLevelThreeTitle(checkedId, treeData, dimensionData)
        }

        return ''
    }
    function getRenameLevelTwoTitle(checkedId, treeData, dimensionData) {
        let dimensionType = getDimensionTypeFromCheckedNode(checkedId, treeData)

        if (dimensionType > -1) {
            return `Changing Name of ${getDimensionAliasFromCheckedNodeId(checkedId, dimensionData, treeData)} -> ${getNameCheckedNode(checkedId, treeData)}`
        }

        return ''
    }
    function getRenameLevelThreeTitle(checkedId, treeData, dimensionData) {
        let dimensionType = getDimensionTypeFromCheckedNode(checkedId, treeData)

        if (dimensionType > -1) {
            let treeStructureIndex = getLevelOneTreeStructureIndex(dimensionData, dimensionType)
            let segmentIndex = getIndexOfSegmentWithMatchingId(checkedId, treeData[treeStructureIndex]["children"])
            return `Changing Name of ${getDimensionAliasFromCheckedNodeId(checkedId, dimensionData, treeData)} -> ${getSegmentNameFromIndexes(treeData, treeStructureIndex, segmentIndex)} -> ${getNameCheckedNode(checkedId, treeData)}`
        }

        return ''
    }
    function stringAreEqual(str1, str2) {
        return str1.toLowerCase().trim() === str2.toLowerCase().trim()
    }
    function stringIsEmpty(str) { 
        return str.trim() === ''
    }
    function isValidInteger(str) {
        let parsed = parseInt(str, 10);
        return !isNaN(parsed) && parsed.toString() === str.trim();
      }
    //#endregion

    return (
        <>
            <Paper elevation={3} className="dbFunctionsPaper">
                <h3>Add Segments</h3>
                <FormControl className="field" fullWidth>
                    <InputLabel id="select-databse-label">Select Database</InputLabel>
                    <Select
                        labelId="select-databse-label"
                        id="select-databse"
                        label="Select Database"
                        onChange={handleDatabase}
                        defaultValue={sqLDbList.length > 0 ? sqLDbList[0].id : -1}
                        value={databaseId}
                        required
                        fullWidth
                        disabled={isLoadingRequest}>
                            <MenuItem value={-1}>Select Database</MenuItem>
                            { sqLDbList.map((x) => x && <MenuItem key={x.id} value={x.id}>{x.dBname}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl className="field" fullWidth>
                    <InputLabel id="select-data-source-label">Select Data Source</InputLabel>
                    <Select
                        labelId="select-data-source-label"
                        id="select-data-source"
                        label="Select Data Source"
                        onChange={handleDataSourceChange}
                        value={dataSourceSystemName}
                        defaultValue={''}
                        required
                        fullWidth
                        disabled={isLoadingRequest}>
                            <MenuItem value={''}>Select Data Source</MenuItem>
                            { dataSources.map((x) => x && <MenuItem key={x.key} value={x.key}>{x.value}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl className="field" fullWidth>
                    <Box sx={{ width: '100%', bgcolor: 'background.paper' }}>
                        <SuperTreeview
                            key={keyId}
                            data={segmentationData}
                            onUpdateCb={(updatedData)=>{
                                setSegmentationData(updatedData)
                                fireOnSaveEvent(databaseId, dataSourceSystemName, updatedData)
                            }}
                            isDeletable={(node, depth) => depth > 0}
                            onDeleteCb={(node, updatedData, depth)=>{
                                setSegmentationData(updatedData)
                                fireOnSaveEvent(databaseId, dataSourceSystemName, updatedData)
                                return true
                            }}
                            onCheckToggleCb={(arrayOfNodes, depth)=>{
                                if (arrayOfNodes && arrayOfNodes.length > 0) {
                                    let selectedId = arrayOfNodes[0]["id"]
                                    let checked = arrayOfNodes[0]["isChecked"]
                                    if (checked) {
                                        setCurrentCheckedNodeId(selectedId)
                                        setCurrentCheckedDepth(depth)
                                    }
                                }
                            }}
                            isExpandable={(node, depth)=>(depth===0) || (depth===1)}
                            isCheckable={(node, depth)=>true}
                            noChildrenAvailableMessage=""
                        />
                    </Box>
                </FormControl>
                <FormControl className="field" fullWidth>
                    <ButtonGroup variant="contained" aria-label="Basic button group" disabled={dimensions.length === 0}>
                        <Button
                            disabled={!shouldTheAddSegmentButtonBeEnabled(dimensions, currentCheckedNodeId)}
                            onClick={(e) => {
                                e.preventDefault()
                                setAddingNewSegment(true)
                            }}>
                            New Segment
                        </Button>
                        <Button disabled={shouldDisableNewValueButton(currentCheckedDepth)} onClick={(e) => {
                            e.preventDefault()
                            let dimensionType = getDimensionTypeFromCheckedNode(currentCheckedNodeId, segmentationData)
                            if (dimensionType > -1) {
                                setAddingNewSegmentValue(true)
                            }
                        }}>
                            New Value
                        </Button>
                        <Button disabled={shouldDisableRenameButton(currentCheckedDepth)} onClick={(e) => {
                            e.preventDefault()
                            let dimensionType = getDimensionTypeFromCheckedNode(currentCheckedNodeId, segmentationData)
                            if (dimensionType > -1) {
                                if (currentCheckedDepth === 1) {
                                    let renameElementValue = getNameCheckedNode(currentCheckedNodeId, segmentationData)
                                    setRenameValue(renameElementValue)
                                    setRenamingElement(true)
                                } else if (currentCheckedDepth === 2) {
                                    let renameElementValue = getNameCheckedNode(currentCheckedNodeId, segmentationData)
                                    setRenameValue(renameElementValue)
                                    setRenamingElement(true)
                                }
                            }
                        }}>
                            Rename
                        </Button>
                    </ButtonGroup>
                </FormControl>
            </Paper>
            <Dialog open={addingNewSegment && dimensions.length > 0} maxWidth="sm">
                <DialogTitle style={{ textAlign: "center" }}>Add New Segment For {getDimensionAlias(dimensions, currentCheckedNodeId)}</DialogTitle>
                <DialogContent style={{ textAlign: "center" }}>
                    <TextField
                        size="small"
                        label="New Segment Name"
                        fullWidth
                        value={newSegmentName}
                        onChange={(e) => {
                            e.preventDefault()
                            setNewSegmentName(e.target.value)
                        }}
                    />
                </DialogContent>
                <DialogActions disableSpacing={false} style={{ justifyContent: "center" }}>
                    <Button className="btn" variant="contained" disabled={shouldDisableAddNewSegmentAddButton(newSegmentName, currentCheckedNodeId, segmentationData, dimensions)} onClick={(e) => {
                        e.preventDefault()
                        let dimensionLookup = getDimentionFromDimensionType(dimensions, currentCheckedNodeId)
                        let treeStructureIndex = getLevelOneTreeStructureIndex(dimensions, currentCheckedNodeId)
                        if (dimensionLookup && treeStructureIndex > -1) {
                            let newTreeData = {
                                id: `${dimensionLookup["typeOfDimension"]}-${segmentationData[treeStructureIndex]["children"].length}`,
                                name: newSegmentName,
                                isExpanded: true,
                                isChecked: false,
                                value: '',
                                dimensionType: dimensionLookup["typeOfDimension"],
                                oldName: '',
                                dataType: 'string',
                                children: []
                            }
    
                            let copied = segmentationData
                            copied[treeStructureIndex]["children"].push(newTreeData)
    
                            setSegmentationData(copied)
                            fireOnSaveEvent(databaseId, dataSourceSystemName, copied)
                            setNewSegmentName('')
                            setKeyId(prevKeyId => prevKeyId + 1)
                            setAddingNewSegment(false)
                        }
                    }}>
                        Add
                    </Button>
                    <Button className="btn" variant="contained" disabled={false} onClick={(e) => {
                        e.preventDefault()
                        setNewSegmentName('')
                        setAddingNewSegment(false)
                    }}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={addingNewSegmentValue && dimensions.length > 0} maxWidth="sm">
                <DialogTitle style={{ textAlign: "center" }}>Add New Segment Value For {getDimensionAliasFromCheckedNodeId(currentCheckedNodeId, dimensions, segmentationData)} - {getNameCheckedNode(currentCheckedNodeId, segmentationData)}</DialogTitle>
                <DialogContent style={{ textAlign: "center" }}>
                    <TextField
                        size="small"
                        label="New Segment Value"
                        fullWidth
                        value={newSegmentValue}
                        onChange={(e) => {
                            e.preventDefault()
                            setNewSegmentValue(e.target.value)
                        }}
                    />
                </DialogContent>
                <DialogActions disableSpacing={false} style={{ justifyContent: "center" }}>
                    <Button className="btn" variant="contained" disabled={shouldDisableAddNewSegmentAction(newSegmentValue, currentCheckedNodeId, segmentationData, dimensions)} onClick={(e) => {
                        e.preventDefault()
                        let dimensionType = getDimensionTypeFromCheckedNode(currentCheckedNodeId, segmentationData)
                        if (dimensionType > -1) {
                            let treeStructureIndex = getLevelOneTreeStructureIndex(dimensions, dimensionType)
                            let segmentIndex = getLevelTwoTreeStructureIndex(segmentationData, treeStructureIndex, currentCheckedNodeId)
                            
                            let copied = segmentationData
                            let newSegmentValueNode = {
                                id: `${treeStructureIndex}-${segmentIndex}-${copied[treeStructureIndex]["children"][segmentIndex]["children"].length}`,
                                name: newSegmentValue,
                                isChecked: false,
                                segmentName: copied[treeStructureIndex]["children"][segmentIndex]["name"],
                                value: copied[treeStructureIndex]["children"][segmentIndex]["value"],
                                dimensionType: dimensionType,
                                oldName: '',
                                dataType: copied[treeStructureIndex]["children"][segmentIndex]["dataType"],
                                children: []
                            }

                            copied[treeStructureIndex]["children"][segmentIndex]["children"].push(newSegmentValueNode)
                            
                            setSegmentationData(copied)
                            fireOnSaveEvent(databaseId, dataSourceSystemName, copied)
                            setAddingNewSegmentValue(false)
                            setNewSegmentValue('')
                            setKeyId(prevKeyId => prevKeyId + 1)
                        }
                    }}>
                        Add
                    </Button>
                    <Button className="btn" variant="contained" disabled={false} onClick={(e) => {
                        e.preventDefault()
                        setNewSegmentValue('')
                        setAddingNewSegmentValue(false)
                    }}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={renamingElement && dimensions.length > 0} maxWidth="sm">
                <DialogTitle style={{ textAlign: "center" }}>{getRenameTitle(currentCheckedDepth, currentCheckedNodeId, segmentationData, dimensions)}</DialogTitle>
                <DialogContent style={{ textAlign: "center" }}>
                    <TextField
                        size="small"
                        label="Rename"
                        fullWidth
                        value={renameValue}
                        onChange={(e) => {
                            e.preventDefault()
                            setRenameValue(e.target.value)
                        }}
                    />
                </DialogContent>
                <DialogActions disableSpacing={false} style={{ justifyContent: "center" }}>
                    <Button className="btn" variant="contained" disabled={shouldDisableRenameAdd(renameValue, currentCheckedDepth, currentCheckedNodeId, segmentationData, dimensions)} onClick={(e) => {
                        e.preventDefault()
                        let dimensionType = getDimensionTypeFromCheckedNode(currentCheckedNodeId, segmentationData)
                        if (dimensionType > -1) {
                            if (currentCheckedDepth === 1) {
                                let treeStructureIndex = getLevelOneTreeStructureIndex(dimensions, dimensionType)
                                let segmentIndex = getLevelTwoTreeStructureIndex(segmentationData, treeStructureIndex, currentCheckedNodeId)

                                let copied = segmentationData
                                let oldName = copied[treeStructureIndex]["children"][segmentIndex]["name"]
                                if (copied[treeStructureIndex]["children"][segmentIndex]["oldName"] === '') {
                                    copied[treeStructureIndex]["children"][segmentIndex]["oldName"] = oldName
                                }
                                copied[treeStructureIndex]["children"][segmentIndex]["name"] = renameValue
                                setSegmentationData(copied)
                                fireOnSaveEvent(databaseId, dataSourceSystemName, copied)
                                setKeyId(prevKeyId => prevKeyId + 1)
                            } else if (currentCheckedDepth === 2) {
                                let treeStructureIndex = getLevelOneTreeStructureIndex(dimensions, dimensionType)
                                let segmentIndex = getIndexOfSegmentWithMatchingId(currentCheckedNodeId, segmentationData[treeStructureIndex]["children"])
                                let valueIndex = getLevelTwoTreeStructureIndex(segmentationData[treeStructureIndex]["children"], segmentIndex, currentCheckedNodeId)
                                
                                let copied = segmentationData
                                let oldName = copied[treeStructureIndex]["children"][segmentIndex]["children"][valueIndex]["name"]
                                if (copied[treeStructureIndex]["children"][segmentIndex]["children"][valueIndex]["oldName"] === '') {
                                    copied[treeStructureIndex]["children"][segmentIndex]["children"][valueIndex]["oldName"] = oldName
                                }
                                copied[treeStructureIndex]["children"][segmentIndex]["children"][valueIndex]["name"] = renameValue
                                setSegmentationData(copied)
                                fireOnSaveEvent(databaseId, dataSourceSystemName, copied)
                                setKeyId(prevKeyId => prevKeyId + 1)
                            }
                            setRenameValue('')
                            setRenamingElement(false)
                        }
                    }}>
                        Add
                    </Button>
                    <Button className="btn" variant="contained" disabled={false} onClick={(e) => {
                        e.preventDefault()
                        setRenamingElement(false)
                    }}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default SegmentationBuilder